import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { promoService, parameterService } from 'ui/services';
import { useRouter } from 'next/router';
import ErrorScreen from 'ui/components/molecules/ErrorScreen';
import Layout from 'ui/components/templates/Layout';
import { convertSlugToNormalText } from 'ui/helpers/utils';
import constants from '@/constants';
import useEntryPage from 'ui/hooks/useEntryPage';
import entryPageValues from 'ui/constants/entryPageValues';
import useIsNativeView from 'ui/hooks/useIsNativeView';
import handlePageError from 'ui/helpers/utils/handlePageError';
import getNativeRedirection from 'ui/helpers/utils/getNativeRedirection';
import { IMetadata } from 'ui/components/atoms/Metadata/interface';
import { IFaq } from 'ui/components/molecules/FaqAccordion';
import { NewsHero } from 'ui/interface';
import useViewPromo from 'ui/hooks/emarsys/promo/useViewPromo';
import useSendGOPromo from 'ui/hooks/emarsys/promo/useSendGOPromo';
import { FEATURE } from 'ui/constants/emarsys/feature';
import { PAGE_NAME_PROMO } from 'ui/constants/emarsys/promo';
import { useDispatch } from 'react-redux';
import { setPageBranch } from 'ui/store/googleTagManager/actions';

const PromoListingPage = dynamic(() => import('ui/components/templates/PromoListingPage'), {
  ssr: true,
});
const Metadata = dynamic(() => import('ui/components/atoms/Metadata'), { ssr: true });
const StructuredData = dynamic(() => import('ui/components/atoms/StructuredData'), { ssr: true });

const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;
const PUBLIC_BASEPATH_URL = process.env.NEXT_PUBLIC_BASEPATH_URL;

interface PromoListByCategoryPageProps {
  promoHero: NewsHero;
  errorCode?: number;
  categoryList?: {
    name: string;
    path: string;
    priority: string;
    description: string;
  }[];
  metadata?: IMetadata;
  faqs: {
    title: string;
    faqList: IFaq[];
  };
  promoList: {
    id?: string;
    contentCategory?: {
      name?: string;
    };
    slug?: string;
    titlePage?: string;
    articleType?: string;
    startDate?: string;
    endDate?: string;
    heroImageLink?: string;
    altImage?: string;
    tag: {
      id?: string;
      tagName?: string;
    };
  }[];
  page: number;
  category: string;
  totalData: number;
  totalPages: number;
  info: {
    title: string;
    desc: string;
  };
  paginationBaseUrl: string;
  pageSize: number;
}

const PromoListByCategoryPage = (props: PromoListByCategoryPageProps) => {
  const [pageError, setPageError] = useState(props.errorCode);

  const route = useRouter();
  const promoCategory: string = route?.query?.kategori as string;

  const isNativeView = useIsNativeView();

  const { saveEntryPage } = useEntryPage();
  const dispatch = useDispatch();

  const breadcrumbItems = [
    {
      name: 'Home',
      item: `${PUBLIC_URL}`,
      path: `${PUBLIC_URL}`,
    },
    {
      name: 'Promo',
      item: `${PUBLIC_BASEPATH_URL}`,
      path: `${PUBLIC_BASEPATH_URL}`,
    },
    {
      name: convertSlugToNormalText(promoCategory),
    },
  ];

  useEffect(() => {
    dispatch(setPageBranch('promo page'));
  }, []);
  useEffect(() => {
    if (!props?.errorCode) {
      sessionStorage.removeItem('entryPage');
      saveEntryPage(entryPageValues.PROMO_LISTING_PAGE);
    }

    if (typeof window !== 'undefined') {
      const isCategoryValid = props?.categoryList?.find(
        (data) => data?.path === `/${route?.query?.kategori}`
      );
      if (!isCategoryValid) {
        setPageError(404);
      }
    }
  }, [props, route, saveEntryPage]);

  if (pageError) {
    return <ErrorScreen errorCode={pageError} withLayout />;
  }

  useViewPromo({
    pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
    pageTitle: props?.promoHero?.promoTitle,
    breadcrumbItems: breadcrumbItems,
    newParams: {
      ...(breadcrumbItems?.[2]?.name && { menu_cat_lvl2: breadcrumbItems[2].name.toLowerCase() }),
      page_category: props.category?.toLowerCase(),
      page_number: props?.page,
    },
  });

  useSendGOPromo();

  return (
    <Layout isNativeView={isNativeView}>
      {/* Metadata page */}
      <Metadata {...props.metadata} />
      {/* Data structure for SEO */}
      <StructuredData faqs={props.faqs.faqList} breadcrumbs={breadcrumbItems} />
      {/* Promo listing page */}
      <PromoListingPage
        {...props}
        breadcrumbItems={breadcrumbItems}
        feature={FEATURE.LISTING_PROMO}
      />
      ;
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const { params, query, res } = context;
  const paginationBaseUrl = process.env.NEXT_PUBLIC_BASEPATH_URL + `/${params.kategori}`;
  const { LIMIT_PROMO_PER_PAGE: pageSize } = constants;

  res.setHeader('Cache-Control', 'public, max-age=3600');

  // Redirect if URL is not lowercase
  if (params.kategori !== params.kategori.toLowerCase()) {
    return {
      redirect: {
        destination: `/${params.kategori.split(' ').join('-').toLowerCase()}`,
        statusCode: 301,
      },
    };
  }

  // Handle redirection for native view
  const { isRedirecting, redirectUrl } = getNativeRedirection(context, ['kategori']);
  if (isRedirecting) {
    return {
      redirect: {
        destination: redirectUrl,
        statusCode: 301,
      },
    };
  }

  const category = params.kategori.split('-').join(' ').toUpperCase();
  const page = parseInt(query.page) - 1 || 0;

  /* --------------------------- GET PROMO HERO -------------------------- */
  let promoHero = null;
  let promoHeroResult = null;
  try {
    const promoHeroPromise = promoService.getPromoHeroV2(
      {
        contentCategory: params.kategori === 'servis' ? 'service' : params.kategori,
      },
      true
    );
    const [promoHeroResults] = await Promise.all([promoHeroPromise]);
    promoHero = promoHeroResults.data?.details[0]?.headerCms;
    promoHeroResult = promoHeroResults;
  } catch (error) {
    console.log(error);
  }

  try {
    const promoListPromise = promoService.getPromoList(
      {
        category,
        page,
        size: constants.LIMIT_PROMO_PER_PAGE,
      },
      true
    );
    const categoryListPromise = parameterService.getCategoryType('promo', true);

    const [promoListResult, categoryListResult] = await Promise.all([
      promoListPromise,
      categoryListPromise,
    ]);

    const promoList = promoListResult.data.content;

    const faqs = {
      title: promoHeroResult?.data?.faqTitlePage || '',
      faqList: promoHeroResult?.data?.faqs || [],
    };
    const totalData = promoListResult.data.totalElements;
    const totalPages = promoListResult.data.totalPages;
    const categoryList = [
      {
        name: 'SEMUA',
        description: null,
        priority: 0,
        path: '',
      },
      ...categoryListResult.data.map((cat) => ({
        ...cat,
        path: `/${cat.name.split(' ').join('-').toLowerCase()}`,
      })),
    ];

    const robotsTag = promoHeroResult?.data?.seoRobotTag;

    const metadata = {
      title: promoHeroResult?.data?.seoTitleTag || null,
      description: promoHeroResult?.data?.seoMetaDescription || null,
      keywords: promoHeroResult?.data?.seoKeyword || null,
      robots: process.env.ENV === 'PRD' ? robotsTag : 'noindex, nofollow',
    };

    if (promoHero) {
      promoHero.promoTitle = promoHeroResult?.data?.titlePage || '';
      promoHero.promoDescription = promoHeroResult?.data?.description || '';
    }

    // Info content
    const info = {
      title: promoHeroResult?.data?.subTitlePage || '',
      desc: promoHeroResult?.data?.subDescription || '',
    };

    return {
      props: {
        promoHero,
        promoList,
        faqs,
        categoryList,
        page: page + 1,
        category,
        totalData,
        totalPages,
        metadata,
        info,
        paginationBaseUrl,
        pageSize,
      },
    };
  } catch (err) {
    console.log(err);

    if (err?.response?.data?.status === 404) {
      context.res.statusCode = 404;
    } else {
      context.res.setHeader('Retry-After', '3600');
      context.res.statusCode = 503;
    }
    return handlePageError(err);
  }
}

export default PromoListByCategoryPage;
